import { DraftShape, Shape, Storey } from '@shared/models/design/storey.model';
import {
  closeCoordinatesPolygon,
  convertMetricOffsetToCoordinate,
} from '@shared/helpers/geometry/coordinates.helpers';
import { point2dsToCoordinates } from '@shared/helpers/design/shape.helpers';
import {
  getEmptyBuildingHitTargetPolygon,
  getStoreyById,
  getStoreyElevation,
  getStoreyIndex,
  isEmptyBuildingGeometry,
} from '@shared/helpers/design/storey.helpers';
import { Building } from '@shared/models/design/building-design.model';
import { createShape } from '@shared/helpers/design/factories/shape.factory';
import { createStorey } from '@shared/helpers/design/factories/storey.factory';
import {
  GeoJSONSource,
  GeoJSONSourceSpecification,
  Map,
  MapMouseEvent,
} from 'mapbox-gl';
import { Coordinate } from '@shared/models/geography.model';
import { Point2D } from '@shared/models/geometry.model';
import {
  MapStoreyFeature,
  MapStoreyGeoJson,
  MapStoreyPointFeature,
  MapStoreyPolygonFeature,
  MapStoreyProperties,
  MapStoreyProperty,
} from '../models/map-feature.models';
import { ItemOrItemId } from '@shared/models/type-helpers.model';
import { getId } from '@shared/helpers/object.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { designGuards } from '@shared/helpers/design/design-typeguards.helpers';
import { mapGuards } from './map-guards.helpers';
import {
  getCutoutShapes,
  getShapeCenter,
  getSolidShapes,
} from '@shared/helpers/design/cutout.helpers';
import { isPointInPolygon } from '@shared/helpers/geometry/geometry.helpers';
import {
  MapHoverState,
  MapHoverStateType,
  MapHoverStateLine,
  MapSelections,
} from '../models/map-observables.models';
import {
  BUILDING_SOURCE_ID,
  SourceId,
  mapLayers,
  mapLayerOrder,
} from './map-config.constants';
import { moveLayerBeforeIfNeeded } from './map-layer-order.helpers';

/**
 * Convert a coordinate to a map position
 * @param coordinate - The coordinate to convert to a map position
 * @returns The map position as [lng, lat]
 */
const coordinateToMapPosition = ({ lng, lat }: Coordinate): number[] => [
  lng,
  lat,
];

export type StoreyTransparencyResolver = (storey: Storey) => boolean;

/**
 * Resolve storeys that should render transparently when a mezzanine is selected.
 */
/**
 * Resolve storeys that should render "transparent" whenever a mezzanine is selected.
 *
 * This is shared between GeoJSON styling and WebGL overlays so both remain consistent.
 */
export const buildTransparentStoreyResolver = (
  storeys: Storey[],
  selectedStoreyId: string | undefined,
): StoreyTransparencyResolver => {
  const selectedIndex =
    selectedStoreyId !== undefined
      ? getStoreyIndex(storeys, selectedStoreyId)
      : -1;
  if (selectedIndex < 0) return () => false;
  const selectedStorey = storeys[selectedIndex];
  if (!selectedStorey?.mezzanine) return () => false;
  const parentIndex = resolveParentStoreyIndex(storeys, selectedIndex);
  const firstHiddenIndex = parentIndex ?? selectedIndex + 1;
  const transparentIds = new Set(
    storeys
      .slice(firstHiddenIndex)
      .map((candidate) => candidate.id)
      .filter((id) => id !== selectedStoreyId),
  );
  return (storey: Storey) => transparentIds.has(storey.id);
};

const resolveParentStoreyIndex = (
  storeys: Storey[],
  mezzanineIndex: number,
): number | undefined => {
  for (let index = mezzanineIndex - 1; index >= 0; index -= 1) {
    const candidate = storeys[index];
    if (designGuards.is.mezzanineStorey(candidate)) continue;
    return index;
  }
  return undefined;
};

type StoreyPropertyOptions = {
  transparent?: boolean;
};

/**
 * Convert a shape to a polygon coordinates
 * @param shape - The shape to convert to a polygon coordinates
 * @returns The polygon coordinates as [[lng, lat], [lng, lat], ...]
 */
const getMapPolygonCoordinatesFromShape = (
  shape: Shape | DraftShape,
  origin: Coordinate,
): number[][] =>
  closeCoordinatesPolygon(point2dsToCoordinates(origin, shape)).map(
    coordinateToMapPosition,
  );

const getMapStoreyProperties = (
  storeys: Storey[],
  storey: Storey,
  selectedStoreyId: string | undefined,
  options?: StoreyPropertyOptions,
): MapStoreyProperties => {
  const index = getStoreyIndex(storeys, storey);
  const elevation = getStoreyElevation(storeys, storey);
  return {
    [MapStoreyProperty.StoreyHeight]: storey.height,
    [MapStoreyProperty.Elevation]: elevation,
    [MapStoreyProperty.TopHeight]: elevation + storey.height,
    [MapStoreyProperty.Level]: index,
    [MapStoreyProperty.Mezzanine]: Boolean(storey.mezzanine),
    [MapStoreyProperty.Selected]: storey.id === selectedStoreyId,
    [MapStoreyProperty.Hover]: false,
    [MapStoreyProperty.Transparent]: Boolean(options?.transparent),
  };
};

/**
 * Convert an array of features to a GeoJSON object
 * @param features - An array of features to convert to a GeoJSON object
 * @returns A const that satisfies the GeoJSONSourceSpecification type. Note that data might be of several types so that why we use a const.
 */
const getGeoJsonFromFeatures = <T extends MapStoreyFeature>(
  features: T[],
): MapStoreyGeoJson<T> => {
  return {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features,
    },
  } as const satisfies GeoJSONSourceSpecification; // Validate that we fit GeoJSONSourceSpecification type
};

/**
 * Get a GeoJSON polygon object from the storeys (used to render building)
 * @param storeys
 * @returns
 */
export const getMapStoreyPolygonsFromStoreys = (
  storeys: Storey[],
  selectedStoreyOrId: ItemOrItemId<Storey> | undefined,
  origin: Coordinate,
  buildingId?: string,
) => {
  const selectedStoreyId = selectedStoreyOrId
    ? getId(selectedStoreyOrId)
    : undefined;
  const resolveTransparent = buildTransparentStoreyResolver(
    storeys,
    selectedStoreyId,
  );
  // Show polygons from all storeys
  const features = storeys.flatMap((storey) => {
    const properties = getMapStoreyProperties(
      storeys,
      storey,
      selectedStoreyId,
      { transparent: resolveTransparent(storey) },
    );
    const solids = getSolidShapes(storey).filter(
      (shape) => (shape.points?.length ?? 0) >= 3,
    );
    const cutouts = getCutoutShapes(storey).filter(
      (shape) => (shape.points?.length ?? 0) >= 3,
    );

    return solids.map((solid) => {
      const holeCoordinates = cutouts
        .filter((cutout) => {
          const center = getShapeCenter(cutout);
          return isPointInPolygon(center, solid.points);
        })
        .map((cutout) => getMapPolygonCoordinatesFromShape(cutout, origin));
      const feature: MapStoreyPolygonFeature = {
        type: 'Feature',
        id: solid.id,
        properties: {
          ...properties,
          id: solid.id,
          ...(buildingId ? { buildingId } : {}),
        },
        geometry: {
          type: 'Polygon',
          coordinates: [
            getMapPolygonCoordinatesFromShape(solid, origin),
            ...holeCoordinates,
          ],
        },
      };
      return feature;
    });
  });
  return getGeoJsonFromFeatures(features);
};

/**
 * Static footprints for non-selected buildings. Empty buildings get a square
 * hit target so they can be selected again after the user clicks a sibling.
 * @param buildings - Non-selected buildings
 * @param selectedStoreys - Storeys of the selected building, used to place hit targets
 * @param origin - Design location used for metric-to-lng/lat conversion
 */
export const getOtherBuildingMapPolygons = (
  buildings: Building[],
  selectedStoreys: Storey[],
  origin: Coordinate,
): MapStoreyPolygonFeature[] =>
  buildings.flatMap((building) => {
    if (!isEmptyBuildingGeometry(building)) {
      return getMapStoreyPolygonsFromStoreys(
        building.storeys,
        undefined,
        origin,
        building.id,
      ).data.features;
    }
    const polygon = getEmptyBuildingHitTargetPolygon(
      selectedStoreys,
      buildings,
      building.id,
    );
    if (!polygon) return [];
    return getMapStoreyPolygonsFromStoreys(
      [createStorey({ shapes: [createShape(polygon)] })],
      undefined,
      origin,
      building.id,
    ).data.features.map((feature) => ({
      ...feature,
      properties: {
        ...feature.properties,
        emptyHitTarget: true,
      },
    }));
  });

/**
 * Get a GeoJSON point object from the storeys (used to render storey points)
 * @param storeys
 * @param selectedStoreyOrId
 * @param draftShape - Pass in draft shape to only include points from that shape
 * @returns
 */
export const getMapStoreyPointsFromStoreys = (
  storeys: Storey[],
  selections: MapSelections,
  hoverState: MapHoverState | undefined,
  draftShape: DraftShape | undefined,
  origin: Coordinate,
  options?: {
    includeInsertCandidate?: boolean;
  },
) => {
  const storeyId = selections?.storeyId;
  const storey = getStoreyById(storeys, storeyId);
  if (!storey) {
    return getGeoJsonFromFeatures([]);
  }
  const includeInsertCandidate = Boolean(options?.includeInsertCandidate);
  const shapes = draftShape ? [draftShape] : storey.shapes;
  const resolveTransparent = buildTransparentStoreyResolver(storeys, storeyId);
  const properties = getMapStoreyProperties(storeys, storey, storeyId, {
    transparent: resolveTransparent(storey),
  });
  // Henrik: keep point elevation consistent across Mapbox hit-testing and our WebGL rendering.
  const elevation = properties[MapStoreyProperty.Elevation];

  // Iterate through shapes to preserve shapeId and point index within each shape
  const features =
    shapes.filter(guards.is.defined).flatMap((shape) => {
      const shapeCoords = getMapPolygonCoordinatesFromShape(shape, origin);
      // Remove the last coordinate if it's a duplicate of the first (closed polygon)
      const first = shapeCoords[0];
      const last = shapeCoords[shapeCoords.length - 1];
      const isClosed =
        shapeCoords.length > 1 &&
        !!first &&
        !!last &&
        first[0] === last[0] &&
        first[1] === last[1];
      const openCoords =
        isClosed && shapeCoords.length > 1
          ? shapeCoords.slice(0, -1)
          : shapeCoords;

      return openCoords.map((coordinates, index) => {
        const featureId = `${shape.id}:${index}`;
        const isDraft = designGuards.is.draftShape(shape);
        const matchesShape = selections?.shapeId === shape.id;
        const isPointSelection =
          matchesShape && Boolean(selections?.pointIndexes?.includes(index));
        const isShapeSelection =
          matchesShape && !selections?.pointIndexes?.length;
        // Highlight every point whenever the entire shape is selected.
        const isSelected = isShapeSelection || isPointSelection;
        const isHovering =
          (hoverState?.type === MapHoverStateType.Point &&
            hoverState.shapeId === shape.id &&
            hoverState.index === index) ||
          (hoverState?.type === MapHoverStateType.DraftPoint &&
            isDraft &&
            hoverState.index === index &&
            (hoverState.shapeId === undefined ||
              hoverState.shapeId === shape.id));
        const feature: MapStoreyPointFeature = {
          type: 'Feature',
          id: featureId,
          properties: {
            ...properties,
            [MapStoreyProperty.Elevation]: elevation,
            shapeId: shape.id,
            index: index,
            draft: isDraft,
            [MapStoreyProperty.Selected]: isSelected,
            [MapStoreyProperty.Hover]: isHovering,
          },
          geometry: {
            type: 'Point',
            coordinates,
          },
        };
        return feature;
      });
    }) ?? [];

  if (
    includeInsertCandidate &&
    hoverState?.type === MapHoverStateType.Line &&
    !draftShape
  ) {
    const insertFeature = buildInsertCandidateFeature({
      storeys,
      storey,
      hoverState,
      origin,
    });
    if (insertFeature) {
      features.push(insertFeature);
    }
  }

  return getGeoJsonFromFeatures(features);
};

const getFeatureFromMapEvent = (
  event: MapMouseEvent,
): GeoJSON.Feature | undefined => {
  const feature = event.features?.[0];
  return mapGuards.is.feature.any(feature) ? feature : undefined;
};

export const getPointFeatureFromMapEvent = (
  event: MapMouseEvent,
): MapStoreyPointFeature | undefined => {
  const feature = getFeatureFromMapEvent(event);
  return mapGuards.is.feature.point(feature) ? feature : undefined;
};

export const getDraftPointFeatureFromMapEvent = (
  event: MapMouseEvent,
): MapStoreyPointFeature | undefined => {
  const feature = getPointFeatureFromMapEvent(event);
  return feature?.properties.draft === true ? feature : undefined;
};

export const getPolygonFeatureFromMapEvent = (
  event: MapMouseEvent,
): MapStoreyPolygonFeature | undefined => {
  const feature = getFeatureFromMapEvent(event);
  return mapGuards.is.feature.polygon(feature) ? feature : undefined;
};

/**
 * Apply a GeoJSON object to a source and add the corresponding layers to the map.
 * @param map
 * @param sourceId
 * @param geoJson
 */
export const applyGeoJsonToSource = (
  map: Map,
  sourceId: string,
  geoJson: MapStoreyGeoJson,
): void => {
  const source = map.getSource(sourceId) as GeoJSONSource | undefined;

  if (!source) {
    const isGeoJson = geoJson?.type === 'geojson';
    const enableLineMetrics =
      isGeoJson &&
      (sourceId === BUILDING_SOURCE_ID || sourceId === SourceId.DraftLine);
    const spec = enableLineMetrics
      ? ({ ...geoJson, lineMetrics: true } as const)
      : geoJson;
    map.addSource(sourceId, spec);
  } else {
    source.setData(geoJson.data);
  }

  const layers = Object.values(mapLayers).filter(
    (layer) => layer.source === sourceId,
  );

  // Make sure all layers are added to the map
  for (const layer of layers) {
    if (!map.getLayer(layer.id)) {
      map.addLayer(layer);
    }
  }

  // Ensure layers are ordered according to mapLayerOrder
  ensureLayerOrder(map);
};

/**
 * Ensures layers are ordered according to mapLayerOrder.
 * Layers are moved to match the desired order if they exist.
 */
const ensureLayerOrder = (map: Map): void => {
  for (let i = 0; i < mapLayerOrder.length; i++) {
    const targetLayerId = mapLayerOrder[i];
    if (!targetLayerId || !map.getLayer(targetLayerId)) continue;

    let nextLayerId: string | undefined;
    for (let j = i + 1; j < mapLayerOrder.length; j++) {
      const nextCandidate = mapLayerOrder[j];
      if (!nextCandidate) continue;
      if (map.getLayer(nextCandidate)) {
        nextLayerId = nextCandidate;
        break;
      }
    }

    if (!nextLayerId) continue;
    try {
      moveLayerBeforeIfNeeded(map, targetLayerId, nextLayerId);
    } catch {
      // Layer stack may be mid-reload
    }
  }
};

type InsertCandidateParams = {
  storeys: Storey[];
  storey: Storey;
  hoverState: MapHoverStateLine;
  origin: Coordinate;
};

const buildInsertCandidateFeature = ({
  storeys,
  storey,
  hoverState,
  origin,
}: InsertCandidateParams): MapStoreyPointFeature | undefined => {
  const shape = storey.shapes.find(
    (candidate) => candidate.id === hoverState.shapeId,
  );
  if (!shape?.points?.length) return undefined;
  const points = shape.points;
  const total = points.length;
  const start = points[hoverState.startIndex];
  const end = points[hoverState.endIndex % total];
  if (!start || !end) return undefined;
  const midpoint: Point2D = hoverState.midpoint ?? [
    (start[0] + end[0]) / 2,
    (start[1] + end[1]) / 2,
  ];
  const coordinate =
    hoverState.midpointCoordinate ??
    convertMetricOffsetToCoordinate(origin, midpoint);
  const resolveTransparent = buildTransparentStoreyResolver(storeys, storey.id);
  const properties = getMapStoreyProperties(storeys, storey, storey.id, {
    transparent: resolveTransparent(storey),
  });
  const feature: MapStoreyPointFeature = {
    type: 'Feature',
    id: `insert:${shape.id}:${hoverState.startIndex}-${hoverState.endIndex}`,
    properties: {
      ...properties,
      shapeId: shape.id,
      index: hoverState.endIndex,
      draft: false,
      insertCandidate: true,
      insertStartIndex: hoverState.startIndex,
      insertEndIndex: hoverState.endIndex,
      [MapStoreyProperty.Selected]: false,
      [MapStoreyProperty.Hover]: Boolean(hoverState.insertCandidateActive),
    },
    geometry: {
      type: 'Point',
      coordinates: [coordinate.lng, coordinate.lat],
    },
  };
  return feature;
};
